import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CalendarDays, Users, Globe, Mail } from "lucide-react";
import { VerifiedBadge, MatchRing } from "./kit";

const TYPE_STYLES = {
  "BUYING REQUIREMENT": "text-techblue-light border-techblue/40",
  "DISTRIBUTION OPPORTUNITY": "text-gold border-gold/40",
  "FUNDING REQUIREMENT": "text-emerald-400 border-emerald-500/40",
  "STRATEGIC PARTNERSHIP": "text-violet-300 border-violet-400/40",
  "EXPORT OPPORTUNITY": "text-cyan-300 border-cyan-400/40",
};

export function OpportunityCard({ opp, compact = false, onView }) {
  return (
    <div className="card-surface hairline-gold p-6 flex flex-col gap-4 hover:border-slate-600 transition-colors" data-testid={`opportunity-card-${opp.id}`}>
      <div className="flex items-start justify-between gap-3">
        <span className={`border rounded-sm px-2 py-1 text-[10px] font-mono uppercase tracking-widest ${TYPE_STYLES[opp.type] || "text-slate-400 border-slate-600"}`}>{opp.type}</span>
        {opp.verified && <VerifiedBadge level="BUSINESS VERIFIED" testid={`opportunity-verified-${opp.id}`} />}
      </div>
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-slate-500">{opp.org}</p>
        <h3 className="mt-1.5 font-display text-lg font-semibold text-slate-100 leading-snug">{opp.looking_for}</h3>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
        <span className="flex items-center gap-1.5"><MapPin size={12} className="text-slate-500" />{opp.location}</span>
        <span className="flex items-center gap-1.5 font-mono">{opp.budget}</span>
      </div>
      {!compact && <p className="text-xs text-slate-500 leading-relaxed border-l-2 border-gold/40 pl-3">{opp.why}</p>}
      <div className="mt-auto flex items-center justify-between pt-2 border-t border-slate-800">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{opp.status} · {opp.posted}</span>
        {onView ? (
          <button onClick={() => onView(opp)} data-testid={`opportunity-cta-${opp.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-gold hover:text-gold-light">
            VIEW OPPORTUNITY <ArrowRight size={12} />
          </button>
        ) : (
          <Link to="/opportunities" data-testid={`opportunity-cta-${opp.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-gold hover:text-gold-light">
            VIEW OPPORTUNITY <ArrowRight size={12} />
          </Link>
        )}
      </div>
    </div>
  );
}

export function CompanyCard({ c }) {
  return (
    <div className="card-surface p-6 flex flex-col gap-4 hover:border-slate-600 transition-colors group" data-testid={`company-card-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="w-11 h-11 rounded-sm bg-navy-700 border border-slate-700 flex items-center justify-center font-display font-bold text-gold text-lg">
          {c.name.charAt(0)}
        </div>
        <VerifiedBadge level={c.verified_level} testid={`company-verified-${c.id}`} />
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-slate-100 group-hover:text-gold transition-colors">{c.name}</h3>
        <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-1">{c.sector}</p>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed line-clamp-2">{c.tagline}</p>
      {(c.website || c.email) && (
        <div className="space-y-1 text-[11px] text-slate-500">
          {c.website && <p className="flex items-center gap-1.5" data-testid={`company-website-${c.id}`}><Globe size={11} className="text-gold shrink-0" />{c.website.replace(/^https?:\/\//, "")}</p>}
          {c.email && <p className="flex items-center gap-1.5" data-testid={`company-email-${c.id}`}><Mail size={11} className="text-gold shrink-0" />{c.email}</p>}
        </div>
      )}
      <div className="mt-auto flex items-center justify-between pt-3 border-t border-slate-800 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><MapPin size={12} />{[c.location, c.country].filter(Boolean).join(", ") || "Global"}</span>
        <span className="font-mono text-gold">{c.match?.overall ? `${c.match.overall}% match` : ""}</span>
      </div>
    </div>
  );
}

export function EventCard({ e }) {
  return (
    <div className="card-surface p-6 flex flex-col gap-4 hover:border-slate-600 transition-colors" data-testid={`event-card-${e.id}`}>
      <div className="flex items-center justify-between">
        <span className="border border-slate-700 rounded-sm px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-slate-400">{e.type}</span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-gold">{e.opportunities} opportunities</span>
      </div>
      <h3 className="font-display text-lg font-semibold text-slate-100">{e.name}</h3>
      <div className="space-y-1.5 text-xs text-slate-400">
        <p className="flex items-center gap-2"><CalendarDays size={12} className="text-slate-500" />{e.dates}</p>
        <p className="flex items-center gap-2"><MapPin size={12} className="text-slate-500" />{e.location}</p>
        {e.exhibitors > 0 && <p className="flex items-center gap-2"><Users size={12} className="text-slate-500" />{e.exhibitors.toLocaleString()} exhibitors</p>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {e.buyer_categories.map((b) => (
          <span key={b} className="bg-navy-800 border border-slate-700 rounded-sm px-2 py-0.5 text-[10px] text-slate-400">{b}</span>
        ))}
      </div>
      <Link to="/events" data-testid={`event-cta-${e.id}`} className="mt-auto inline-flex items-center gap-1 text-xs font-semibold text-gold hover:text-gold-light pt-2 border-t border-slate-800">
        EXPLORE EVENT <ArrowRight size={12} />
      </Link>
    </div>
  );
}
